import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "ahora", "llamados",
    "metaAhora", "metaLlamados",
    "soundBtn", "soundHint"
  ]

  connect() {
    console.debug('llamador-alert: connect')
    this.soundEnabled = (localStorage.getItem("llamador_sound_enabled") === "1")
    this.audioCtx = null

    this.lastAhora = this.readAhora()
    this.lastCount = this.readCount()

    this.syncSoundUI()

    // Observadores para detectar cambios por refresh/poll/turbo
    this.observerAhora = new MutationObserver(() => this.onAhoraChanged())
    this.observerLlamados = new MutationObserver(() => this.onLlamadosChanged())

    if (this.hasAhoraTarget) {
      this.observerAhora.observe(this.ahoraTarget, { childList: true, subtree: true })
    }
    if (this.hasLlamadosTarget) {
      this.observerLlamados.observe(this.llamadosTarget, { childList: true, subtree: true })
    }
  }

  disconnect() {
    this.observerAhora?.disconnect()
    this.observerLlamados?.disconnect()
    this.audioCtx?.close?.()
    this.audioCtx = null
  }

  onAhoraChanged() {
    const current = this.readAhora()
    if (!current) return

    if (current.turnoId === this.lastAhora.turnoId && current.estado === this.lastAhora.estado) return

    const prev = this.lastAhora
    this.lastAhora = current

    this.flash(this.ahoraTarget)
    this.playSound()

    console.debug("llamador-alert: onAhoraChanged", { prev, current })
  }

  onLlamadosChanged() {
    const count = this.readCount()
    if (count === null) return
    if (count === this.lastCount) return

    const prev = this.lastCount
    this.lastCount = count

    console.debug("llamador-alert: onLlamadosChanged", { prev, count })
  }

  readAhora() {
    const el = this.metaAhoraTarget || this.element.querySelector('[data-llamador-alert-target="metaAhora"]')
    if (!el) return null
    return {
      turnoId: Number(el.dataset.turnoId || 0),
      estado: String(el.dataset.estado || "sin_turno")
    }
  }

  readCount() {
    const el = this.metaLlamadosTarget || this.element.querySelector('[data-llamador-alert-target="metaLlamados"]')
    if (!el) return null
    return Number(el.dataset.count || 0)
  }

  enableSound() {
    this.soundEnabled = true
    localStorage.setItem("llamador_sound_enabled", "1")

    this.ensureAudioContext(true)
    this.syncSoundUI()

    console.debug('llamador-alert: enableSound')
    this.beep(880, 0.08, 160)
  }

  syncSoundUI() {
    if (!this.hasSoundBtnTarget) return

    if (this.soundEnabled) {
      this.soundBtnTarget.textContent = "🔊 Sonido activado"
      this.soundBtnTarget.classList.remove("btn-outline-primary")
      this.soundBtnTarget.classList.add("btn-success")
      if (this.hasSoundHintTarget) this.soundHintTarget.textContent = "Listo ✅"
    } else {
      this.soundBtnTarget.textContent = "🔊 Activar sonido"
      this.soundBtnTarget.classList.add("btn-outline-primary")
      this.soundBtnTarget.classList.remove("btn-success")
      if (this.hasSoundHintTarget) this.soundHintTarget.textContent = "(requerido por el navegador)"
    }
  }

  playSound() {
    if (!this.soundEnabled) return
    this.ensureAudioContext(false)

    if (!this.audioCtx || this.audioCtx.state !== "running"){
      console.debug('llamador-alert: playSound - audioCtx not running', this.audioCtx && this.audioCtx.state)
      return
    }

    this.beep(880, 0.06, 180)
    setTimeout(() => this.beep(660, 0.05, 140), 220)
  }

  ensureAudioContext(fromUserGesture) {
    if (!this.audioCtx) {
      this.audioCtx = new (window.AudioContext || window.webkitAudioContext)()
    }
    if (this.audioCtx.state === "suspended" && fromUserGesture) {
      this.audioCtx.resume().catch(() => {})
    }
  }

  beep(freq = 880, gain = 0.06, ms = 160) {
    try {
      const o = this.audioCtx.createOscillator()
      const g = this.audioCtx.createGain()
      o.type = "sine"
      o.frequency.value = freq
      g.gain.value = gain
      o.connect(g)
      g.connect(this.audioCtx.destination)
      o.start()
      setTimeout(() => o.stop(), ms)
    } catch (e) {
      console.warn("llamador-alert: beep error:", e)
    }
  }

  flash(el) {
    el.classList.add("llamador-flash")
    clearTimeout(el._flashT)
    el._flashT = setTimeout(() => el.classList.remove("llamador-flash"), 3500)
  }
}
